import { type QAPreviewPackage, type QAPreviewRule } from "@api/rest/types-and-hooks";
import { QARulesScope } from "@pages/QARules/constants";
import { type ToggleValue } from "@pages/QARules/useQARuleApi";

/** What the on/off control for a package or rule shows on a template or org page. */
export interface SwitchState {
  checked: boolean;
  disabled: boolean;
  /** Why it can't be changed here. */
  reason?: string;
}

export type PackageControl = { detach: true } | ({ detach: false } & SwitchState);

const OTHER_SCOPE_LABEL: Record<QARulesScope, string> = {
  [QARulesScope.TEMPLATE]: "Turned off for this org — change it on the org's QA page",
  [QARulesScope.ORGANIZATION]: "Turned off for this template — change it on the template's QA page",
};

const ownPackageRow = (pkg: QAPreviewPackage, scope: QARulesScope) =>
  scope === QARulesScope.TEMPLATE ? pkg.template_enabled : pkg.org_enabled;
const otherPackageRow = (pkg: QAPreviewPackage, scope: QARulesScope) =>
  scope === QARulesScope.TEMPLATE ? pkg.org_enabled : pkg.template_enabled;
const ownRuleRow = (rule: QAPreviewRule, scope: QARulesScope) =>
  scope === QARulesScope.TEMPLATE ? rule.template_enabled : rule.org_enabled;
const otherRuleRow = (rule: QAPreviewRule, scope: QARulesScope) =>
  scope === QARulesScope.TEMPLATE ? rule.org_enabled : rule.template_enabled;

/**
 * A package attached at this level gets a "detach" (one row holds either an attach
 * or an off, so a switch here would silently drop the attachment). Anything else
 * gets a switch that writes an off row (or clears it) at this level. Off rows from
 * the other level are shown but not editable here.
 */
export const packageControl = (pkg: QAPreviewPackage, scope: QARulesScope): PackageControl => {
  if (ownPackageRow(pkg, scope) === true) return { detach: true };
  if (otherPackageRow(pkg, scope) === false) {
    return { detach: false, checked: false, disabled: true, reason: OTHER_SCOPE_LABEL[scope] };
  }
  return { detach: false, checked: ownPackageRow(pkg, scope) !== false, disabled: false };
};

export const ruleControl = (pkg: QAPreviewPackage, rule: QAPreviewRule, scope: QARulesScope): SwitchState => {
  if (pkg.template_enabled === false || pkg.org_enabled === false) {
    return { checked: false, disabled: true, reason: "The whole package is off" };
  }
  if (otherRuleRow(rule, scope) === false) {
    return { checked: false, disabled: true, reason: OTHER_SCOPE_LABEL[scope] };
  }
  return { checked: ownRuleRow(rule, scope) !== false, disabled: false };
};

/** Turning off writes an off row; turning back on clears it rather than writing `true`. */
export const toggleValueFor = (checked: boolean): ToggleValue => (checked ? null : false);
